import { Capacitor } from '@capacitor/core';
import { useEffect, useRef } from 'react';
import { FiDownload } from 'react-icons/fi';
import { toast } from 'sonner';

import { useUpdateAvailable } from '@/hooks/useUpdateAvailable';

import UpdateToast from '../UpdateToast/UpdateToast';

const IS_IOS = Capacitor.getPlatform() === 'ios';

// ponytail: iOS points at TestFlight for BOTH flavors, because both ship from one App
// Store Connect record (appId com.vesselhaven.vh) and neither is publicly listed. The
// day prod goes on the App Store this is a dead button for every prod user — flip the
// prod arm to `itms-apps://apps.apple.com/app/id<APP_STORE_ID>`, keyed off
// `import.meta.env.MODE === 'demo'` (that's what already separates ios:demo/ios:prod).
//
// Android uses the https listing rather than `market://`: Android deep-links it into
// the Play app, and it still lands somewhere real in a browser if Play is missing, so
// there's no unhandled-scheme dead button. appId per capacitor.config.ts.
const STORE = IS_IOS
  ? { label: 'TestFlight', url: 'itms-beta://' }
  : {
      label: 'Google Play',
      url: 'https://play.google.com/store/apps/details?id=com.vesselhaven.vh',
    };

// Capacitor's web view hands both a non-http(s) scheme and an off-origin https URL to
// the OS, which is what launches the store app. @capacitor/browser can't do either —
// it has no scheme support and would open the Play listing in an in-app tab.
const openStore = () => {
  window.location.href = STORE.url;
};

export default function NativeUpdateToast() {
  const available = useUpdateAvailable();
  const toastIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!available || toastIdRef.current) return;

    toastIdRef.current = toast.custom(
      (toastId) => (
        <UpdateToast
          icon={<FiDownload />}
          title="Update available"
          description={`A newer build is ready in ${STORE.label}.`}
          actionLabel={`Open ${STORE.label}`}
          onAction={openStore}
          onDismiss={() => toast.dismiss(toastId)}
        />
      ),
      {
        duration: Infinity,
        onDismiss: () => {
          toastIdRef.current = null;
        },
      },
    );

    return () => {
      if (toastIdRef.current !== null) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = null;
      }
    };
  }, [available]);

  return null;
}
